/**
 * SPEC-034 § 7: variables pasted in one go, as a JSON object ({"NAME": "value"}) or as .env lines (NAME=value,
 * comments and `export ` allowed, quotes removed). Names are what env.example declares; the values never leave the
 * browser here (the portal only splits and checks them).
 */
export interface ImportedVariable { name: string; value: string }

const NAME = /^[A-Za-z_][A-Za-z0-9_]*$/;

function unquote(raw: string): string {
  const v = raw.trim();
  if (v.length >= 2 && ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'")))) {
    const inner = v.slice(1, -1);
    return v.startsWith('"') ? inner.replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\\\/g, '\\') : inner;
  }
  // An unquoted value ends at an inline comment (« # » after a space).
  const hash = v.search(/\s#/);
  return hash >= 0 ? v.slice(0, hash).trim() : v;
}

export function parseEnvImport(text: string): { variables: ImportedVariable[]; errors: string[] } {
  const variables = new Map<string, string>();
  const errors: string[] = [];
  const trimmed = text.trim();
  if (!trimmed) return { variables: [], errors: [] };
  if (trimmed.startsWith('{')) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      return { variables: [], errors: ['json'] };
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return { variables: [], errors: ['json'] };
    for (const [name, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (!NAME.test(name)) { errors.push(name); continue; }
      if (value === null || typeof value === 'object') { errors.push(name); continue; }
      variables.set(name, String(value));
    }
  } else {
    trimmed.split(/\r?\n/).forEach((line, i) => {
      const l = line.trim();
      if (!l || l.startsWith('#')) return;
      const body = l.startsWith('export ') ? l.slice(7).trim() : l;
      const eq = body.indexOf('=');
      const name = eq > 0 ? body.slice(0, eq).trim() : '';
      if (!NAME.test(name)) { errors.push(`${i + 1}`); return; }
      variables.set(name, unquote(body.slice(eq + 1)));
    });
  }
  return { variables: [...variables].map(([name, value]) => ({ name, value })), errors };
}

export type ImportAction = 'secret' | 'plain' | 'unchanged' | 'undeclared' | 'build' | 'immutable' | 'reserved' | 'empty';

/** What the import does with each name, against what env.example declares and what is set already. */
export function planImport(
  variables: ImportedVariable[],
  declared: { name: string; secret: boolean; build: boolean; immutable: boolean; present: boolean | null; value?: string | null; fromCatalogue?: boolean }[],
): { name: string; value: string; action: ImportAction }[] {
  return variables.map(({ name, value }) => {
    const d = declared.find(x => x.name === name);
    let action: ImportAction;
    if (name.startsWith('KANBIOS_')) action = 'reserved';
    else if (!d) action = 'undeclared';
    else if (d.build) action = 'build';
    else if (d.secret) action = !value ? 'empty' : d.immutable && d.present ? 'immutable' : 'secret';
    else if (d.immutable && d.fromCatalogue && d.value !== value) action = 'immutable';
    else action = d.value === value && d.fromCatalogue ? 'unchanged' : 'plain';
    return { name, value, action };
  });
}
